// LabReportPrintReport.js
//
// Same rendering logic as your existing PrintReport.js (Html/cellStyle/
// LayoutTablePrint/TestRows are lifted as-is), but instead of rendering ONE
// test's printData, it takes the whole `reports[]` list from
// BookingReportStatusDTO and renders every test's report, each wrapped in
// its own `.report-page` block with a CSS page-break between them. Because
// everything is mounted under a single React root, calling `window.print()`
// once prints all of them merged into a single PDF/job — exactly what
// PatientReport.js's single-test HandlePrintReport.js does per-test today,
// just fanned out.

import React, { useEffect } from "react";
import { serverConfig } from "../ApiSearchPath/ApiSearchPath";

// ---------------------------------------------------------------------------
// Helpers (identical to PrintReport.js)
// ---------------------------------------------------------------------------
const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return isNaN(date.getTime())
    ? "—"
    : `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
};

const Html = ({ value }) =>
  value ? <span dangerouslySetInnerHTML={{ __html: value }} /> : <span>—</span>;

const cellStyle = (align = "left") => ({
  border: "1px solid #000",
  padding: "4px 6px",
  textAlign: align,
});

// ---------------------------------------------------------------------------
// reportLayout -> printable table (same as PrintReport.js)
// ---------------------------------------------------------------------------
const isVisiblePrint = (visibleWhen, values) => {
  if (!visibleWhen) return true;
  const value = values?.[visibleWhen.field];
  if (visibleWhen.equals !== undefined) return value === visibleWhen.equals;
  if (visibleWhen.notEquals !== undefined) return value !== visibleWhen.notEquals;
  if (visibleWhen.truthy) return !!value;
  if (visibleWhen.falsy) return !value;
  return true;
};

const LayoutComponentPrint = ({ component, values }) => {
  if (!isVisiblePrint(component.visibleWhen, values)) return null;

  switch (component.component) {
    case "LABEL":
      return (
        <span
          style={{
            fontWeight: component.bold ? 700 : undefined,
            textDecoration: component.underline ? "underline" : undefined,
          }}
        >
          {component.label || component.text}
        </span>
      );

    case "TEXT_INPUT":
    case "NUMBER_INPUT":
    case "CALCULATED_INPUT":
    case "SELECT":
      return (
        <span>
          {component.label ? <strong>{component.label}: </strong> : null}
          {values?.[component.id] || "—"}
        </span>
      );

    case "CHECKBOX":
      return <span>{values?.[component.id] ? "Yes" : "No"}</span>;

    case "EDITOR":
      return <Html value={values?.[component.id]} />;

    default:
      return null;
  }
};

const LayoutSectionPrint = ({ section, values }) => {
  if (!isVisiblePrint(section.visibleWhen, values)) return null;

  switch (section.type) {
    case "header":
      return (
        <div
          style={{
            fontWeight: 700,
            fontSize: section.fontSize === "medium" ? "13.5px" : "15px",
            textAlign: section.align === "center" ? "center" : "left",
            margin: "10px 0 4px",
          }}
        >
          {section.title}
        </div>
      );

    case "table":
      return (
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 6 }}>
          <tbody>
            {section.rows.map((row, ri) => (
              <tr key={ri}>
                {row.cells.map((cell, ci) => (
                  <td
                    key={ci}
                    colSpan={cell.colSpan || 1}
                    style={{
                      border: "1px solid #ccc",
                      padding: "3px 6px",
                      textAlign: cell.align === "center" ? "center" : "left",
                    }}
                  >
                    {cell.components?.map((c, i) => (
                      <LayoutComponentPrint key={i} component={c} values={values} />
                    ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );

    case "box":
      return (
        <div style={{ padding: "4px 0", marginBottom: 6 }}>
          {section.components.map((c, i) => (
            <div key={i}>
              <LayoutComponentPrint component={c} values={values} />
            </div>
          ))}
        </div>
      );

    default:
      return null;
  }
};

const LayoutTablePrint = ({ layout, values }) => (
  <div style={{ marginBottom: 16 }}>
    {layout.sections?.map((section, i) => (
      <LayoutSectionPrint key={i} section={section} values={values} />
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Test hierarchy rows (same as PrintReport.js)
// ---------------------------------------------------------------------------
const TestRows = ({ subTests }) => {
  if (!subTests?.length) return null;

  return subTests.map((sub, i) => {
    const hasChildren = sub.subSubTests?.length > 0;

    if (hasChildren) {
      return (
        <React.Fragment key={sub.subTestId ?? i}>
          <tr>
            <td colSpan={4} style={{ ...cellStyle(), fontWeight: 600, paddingLeft: 8 }}>
              {sub.subTestName}
            </td>
          </tr>
          {sub.subSubTests.map((subsub, j) => (
            <tr key={subsub.subSubTestId ?? `${i}-${j}`}>
              <td style={{ ...cellStyle(), paddingLeft: 28 }}>{subsub.subTestName}</td>
              <td style={cellStyle()}>
                <Html value={subsub.result} /> {subsub.unit || ""}
              </td>
              <td style={cellStyle()}>
                <Html value={subsub.normalRange} />
              </td>
              <td style={cellStyle()}>{subsub.methodName || "—"}</td>
            </tr>
          ))}
        </React.Fragment>
      );
    }

    return (
      <tr key={sub.subTestId ?? i}>
        <td style={{ ...cellStyle(), paddingLeft: 8 }}>{sub.subTestName}</td>
        <td style={cellStyle()}>
          <Html value={sub.result} /> {sub.unit || ""}
        </td>
        <td style={cellStyle()}>
          <Html value={sub.normalRange} />
        </td>
        <td style={cellStyle()}>{sub.methodName || "—"}</td>
      </tr>
    );
  });
};

// ---------------------------------------------------------------------------
// One test's report body — identical content to PrintReport.js's return,
// minus the patient-info block (that's now rendered once per page, shared
// across every test, via SharedPatientHeader below) and minus its own
// window.print() effect (that now lives once at the top level).
// ---------------------------------------------------------------------------
const SingleTestReportBody = ({ data }) => {
  const hasSubTests = data?.subTests?.length > 0;
  const showTable = data?.showTable;
  const printAsLayout = data?.reportLayout?.printLayoutAsTable === "true";

  return (
    <>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <h4 style={{ margin: 0 }}>{data?.testGroupName || "LABORATORY REPORT"}</h4>
      </div>

      {printAsLayout && data?.reportLayout && (
        <LayoutTablePrint layout={data.reportLayout} values={data?.dynamicValues || {}} />
      )}

      {showTable && (
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16, border: "2px solid #000" }}>
          <thead>
            <tr style={{ background: "#f0f0f0" }}>
              <th style={cellStyle("left")}>Investigation</th>
              <th style={cellStyle("left")}>Result</th>
              <th style={cellStyle("left")}>Normal Range</th>
              <th style={cellStyle("left")}>Method</th>
            </tr>
          </thead>
          <tbody>
            {hasSubTests ? (
              <>
                <tr>
                  <td colSpan={4} style={{ ...cellStyle(), fontWeight: 700 }}>{data?.mainTestName}</td>
                </tr>
                <TestRows subTests={data.subTests} />
              </>
            ) : (
              <tr>
                <td style={{ ...cellStyle(), fontWeight: 700 }}>{data?.mainTestName}</td>
                <td style={cellStyle()}>
                  <Html value={data?.result} />
                </td>
                <td style={cellStyle()}>
                  <Html value={data?.normalRange} />
                </td>
                <td style={cellStyle()}>{data?.method || "—"}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {stripHtml(data?.impression).trim() && (
        <div style={{ marginBottom: 8 }}>
          <strong>Impression:</strong> <Html value={data?.impression} />
        </div>
      )}
      {stripHtml(data?.interpretation).trim() && (
        <div style={{ marginBottom: 8 }}>
          <strong>Interpretation:</strong> <Html value={data?.interpretation} />
        </div>
      )}
      {stripHtml(data?.patientComment).trim() && (
        <div style={{ marginBottom: 8 }}>
          <strong>Comment:</strong> <Html value={data?.patientComment} />
        </div>
      )}

      {!printAsLayout && (() => {
        const extras = data?.reportExtras || [];
        const rendered = new Set();
        const blocks = [];

        extras.forEach((extra) => {
          if (extra.group) {
            if (rendered.has(extra.group)) return;
            rendered.add(extra.group);

            const groupItems = extras.filter((e) => e.group === extra.group);
            const hasAnyValue = groupItems.some((e) => stripHtml(e.value).trim());
            if (!extra.alwaysShow && !hasAnyValue) return;

            const cols = extra.groupColumns || 2;
            const rowCount = Math.ceil(groupItems.length / cols);
            const rows = [];
            for (let r = 0; r < rowCount; r++) {
              const cells = [];
              for (let c = 0; c < cols; c++) {
                const item = groupItems[r + c * rowCount];
                cells.push(
                  <React.Fragment key={c}>
                    <td style={{ padding: "2px 6px" }}>{item ? `${r + c * rowCount + 1}.${item.label}` : ""}</td>
                    <td style={{ padding: "2px 6px" }}>{item ? item.value : ""}</td>
                  </React.Fragment>
                );
              }
              rows.push(<tr key={r}>{cells}</tr>);
            }

            blocks.push(
              <table key={extra.group} style={{ width: "100%", borderCollapse: "collapse", margin: "4px 0 12px" }}>
                <tbody>{rows}</tbody>
                <tfoot>
                  <tr><td colSpan={cols * 2} style={{ borderBottom: "1px solid #000", paddingTop: 6 }} /></tr>
                </tfoot>
              </table>
            );
            return;
          }

          if (!extra.alwaysShow && !stripHtml(extra.value).trim()) return;
          blocks.push(
            <div key={extra.id} style={{ marginBottom: 4 }}>
              <strong>{extra.label}</strong> {extra.value ? <Html value={extra.value} /> : null}
            </div>
          );
        });

        return blocks;
      })()}
    </>
  );
};

// Patient/booking info block — shared, rendered once at the top of every
// page (labs typically want the patient banner repeated per printed sheet).
const SharedPatientHeader = ({ patientDTO, bookingDTO }) => (
  <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
    <tbody>
      <tr>
        <td style={{ padding: "2px 6px" }}><strong>Patient Id:</strong> {patientDTO?.patientNumber || "—"}</td>
        <td style={{ padding: "2px 6px" }}><strong>Date:</strong> {formatDate(bookingDTO?.reportingDate)}</td>
      </tr>
      <tr>
        <td style={{ padding: "2px 6px" }}><strong>Patient Name:</strong> {patientDTO?.patientName || "—"}</td>
        <td style={{ padding: "2px 6px" }}>
          <strong>Age / Sex:</strong> {patientDTO?.age ? `${patientDTO.age} yrs` : "—"} / {patientDTO?.gender || "—"}
        </td>
      </tr>
      <tr>
        <td style={{ padding: "2px 6px" }}><strong>Referred By:</strong> {bookingDTO?.doctorName || "—"}</td>
        <td style={{ padding: "2px 6px" }}><strong>Booking Id:</strong> {bookingDTO?.bookingId || "—"}</td>
      </tr>
    </tbody>
  </table>
);

// ---------------------------------------------------------------------------
// Main export — one merged document, one page per test.
// `reportsData` = output of buildPrintDataList() from LabReportLayoutUtils.js
// (array of the same flat shape PrintReport.js's single `data` prop expects,
// one entry per MakeReportHierarchyDTO).
// ---------------------------------------------------------------------------
const LabReportPrintReport = ({ reportsData, patientDTO, bookingDTO, printWindow }) => {
  useEffect(() => {
    const win = printWindow || window;
    // One timeout for the WHOLE merged doc — not per test — so print()
    // fires exactly once after every page has painted.
    const timer = setTimeout(() => {
      win.focus();
      win.print();
    }, 500);
    return () => clearTimeout(timer);
  }, [printWindow]);

  const headerImg = patientDTO?.headerImg
    ? `${serverConfig.BASE_URL}/uploads/company/${patientDTO.headerImg}`
    : null;
  const footerImg = patientDTO?.footerImg
    ? `${serverConfig.BASE_URL}/uploads/company/${patientDTO.footerImg}`
    : null;

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", fontSize: "13.5px", color: "#111" }}>
      <style>{`
        @media print {
          .report-page { page-break-after: always; }
          .report-page:last-child { page-break-after: auto; }
        }
      `}</style>

      {(reportsData || []).map((data, idx) => (
        <div className="report-page" key={data.makeReportId ?? idx} style={{ padding: "24px" }}>
          {headerImg && (
            <img src={headerImg} alt="" style={{ width: "100%", maxHeight: 100, objectFit: "contain", marginBottom: 8 }} />
          )}
          <SharedPatientHeader patientDTO={patientDTO} bookingDTO={bookingDTO} />
          <SingleTestReportBody data={data} />
          {footerImg && (
            <img src={footerImg} alt="" style={{ width: "100%", maxHeight: 80, objectFit: "contain", marginTop: 8 }} />
          )}
        </div>
      ))}
    </div>
  );
};

export default LabReportPrintReport;